/**
 * API client for the Interaction Audit group (non-member prospect interaction
 * coverage). Backend: oh_revenue_goals Django app, endpoints under
 * /api/v1/interaction_audit/. Every `apiEndpoints.X` reference lives INSIDE a
 * function body (TDZ-safety — mirrors revenueGoals.js), and every fn returns
 * `response.data` (oh-code admin-portal api-client convention).
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, post, put } from '../request'

// Scoped centres for the current user + whether they're HQ (may see the rollup).
const fetchProspectAuditCentres = async () => {
  try {
    const response = await get(`${apiEndpoints.interactionAudit}/centres/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching interaction audit centres')
    throw error
  }
}

// One centre's non-member prospect list + coverage summary.
const fetchProspectAuditChase = async (centreUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.interactionAudit}/chase/alfred`, {
        centre: centreUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching interaction audit chase list')
    throw error
  }
}

// Per-centre coverage rollup + SPOC accountability (HQ/finance only).
const fetchProspectAuditHqRollup = async () => {
  try {
    const response = await get(
      `${apiEndpoints.interactionAudit}/hq-rollup/alfred`
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching interaction audit HQ rollup')
    throw error
  }
}

// Simplified daily coverage board: every non-member prospect who visited on
// `dateStr` (YYYY-MM-DD), with whether the team logged an interaction since.
const fetchProspectAuditDaily = async (dateStr) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.interactionAudit}/daily/alfred`, {
        date: dateStr
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching interaction audit daily board')
    throw error
  }
}

// The HQ-set follow-up window (days) that defines a "gap".
// Coverage per centre over a window, worst first, with a weekly shape for each:
// { start, end, weeks: [{key,label}], centres: [{centre_name, attended, logged,
//   missed, coverage, weeks:[{key,attended,logged,coverage}]}] }.
// Blank dates -> the last month, which is what the tab opens on.
const fetchProspectAuditTrends = async (start, end) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.interactionAudit}/trends/alfred`, {
        start: start || '',
        end: end || ''
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching interaction audit trends')
    throw error
  }
}

const fetchInteractionAuditConfig = async () => {
  try {
    const response = await get(`${apiEndpoints.interactionAudit}/config/alfred`)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching interaction audit config')
    throw error
  }
}

const updateInteractionAuditConfig = async (gapDays) => {
  try {
    const response = await put(`${apiEndpoints.interactionAudit}/config/alfred`, {
      gap_days: gapDays
    })
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in updating interaction audit config')
    throw error
  }
}

// The "Declined subscription" tick-box state for one parent:
// { enabled, active, expires_on, days }. `enabled` false -> the drawer hides
// the box entirely (the deploy-level gate is off on this environment).
const fetchDeclinedMark = async (parentUuid) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.interactionAudit}/declined/alfred`, {
        parent: parentUuid
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching declined-subscription mark')
    throw error
  }
}

// Set (active=true, expires in the configured days) or clear the mark.
const saveDeclinedMark = async (parentUuid, active) => {
  try {
    const response = await post(
      `${apiEndpoints.interactionAudit}/declined/alfred`,
      { parent: parentUuid, active }
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in saving declined-subscription mark')
    throw error
  }
}

export {
  fetchProspectAuditCentres,
  fetchProspectAuditChase,
  fetchProspectAuditHqRollup,
  fetchProspectAuditDaily,
  fetchProspectAuditTrends,
  fetchInteractionAuditConfig,
  updateInteractionAuditConfig,
  fetchDeclinedMark,
  saveDeclinedMark
}
